<template>
  <div class="manufacturer-price-page">
    <el-card shadow="never" class="page-card">
      <template #header>
        <div class="page-header">
          <div>
            <div class="page-title">维保品牌价格</div>
            <div class="page-subtitle">维护精友维保按品牌识别后的实际扣费价格，金额单位为元。</div>
          </div>
          <div class="header-actions">
            <el-button :loading="templateLoading" @click="downloadTemplate">下载模板</el-button>
            <el-upload
              accept=".xlsx,.xls"
              :show-file-list="false"
              :http-request="handleImport"
              :disabled="importing"
            >
              <el-button :loading="importing">导入</el-button>
            </el-upload>
            <el-button :loading="exporting" @click="exportData">导出</el-button>
            <el-button type="primary" @click="openDialog()">新增品牌价格</el-button>
          </div>
        </div>
      </template>

      <el-alert
        title="导入规则：Excel 第一列填品牌/厂家名称，第二列填维保价格(元)；同名品牌存在时覆盖价格。"
        type="info"
        show-icon
        :closable="false"
        class="import-tip"
      />

      <el-form :model="search" label-width="72px" class="search-form">
        <el-row :gutter="12">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="品牌">
              <el-input v-model="search.name" clearable placeholder="请输入品牌或厂家名称" @keyup.enter="loadData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <div class="search-actions">
              <el-button type="primary" @click="loadData">查询</el-button>
              <el-button @click="resetSearch">重置</el-button>
            </div>
          </el-col>
        </el-row>
      </el-form>

      <el-table :data="records" border stripe v-loading="loading" height="calc(100vh - 356px)">
        <el-table-column label="#" type="index" :index="indexMethod" align="center" width="60" />
        <el-table-column prop="name" label="品牌/厂家名称" min-width="180" show-overflow-tooltip />
        <el-table-column label="维保价格" width="140" align="right">
          <template #default="scope">{{ formatPrice(scope.row.price) }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="170" show-overflow-tooltip />
        <el-table-column prop="updateTime" label="更新时间" min-width="170" show-overflow-tooltip />
        <el-table-column fixed="right" label="操作" width="150">
          <template #default="scope">
            <el-button size="small" @click="openDialog(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-box">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          v-model:current-page="page.current"
          v-model:page-size="page.size"
          :total="page.total"
          :page-sizes="[20, 50, 100]"
          @current-change="loadData"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <el-dialog v-model="dialog.visible" :title="dialog.form.id ? '编辑品牌价格' : '新增品牌价格'" width="520px">
      <el-form ref="formRef" :model="dialog.form" :rules="rules" label-width="112px">
        <el-form-item label="品牌/厂家" prop="name">
          <el-input v-model="dialog.form.name" placeholder="例如：比亚迪 / 一汽大众" />
        </el-form-item>
        <el-form-item label="维保价格" prop="priceYuan">
          <el-input-number v-model="dialog.form.priceYuan" :precision="2" :min="0" :controls="false" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  deleteManufacturerFun,
  downloadManufacturerTemplateFun,
  exportManufacturerFun,
  getManufacturerListFun,
  importManufacturerFun,
  saveManufacturerFun,
} from "@/api/manufacturer";

const loading = ref(false);
const saving = ref(false);
const importing = ref(false);
const exporting = ref(false);
const templateLoading = ref(false);
const records = ref([]);
const formRef = ref();
const search = reactive({ name: "" });
const page = reactive({ current: 1, size: 20, total: 0 });
const dialog = reactive({ visible: false, form: {} });
const rules = {
  name: [{ required: true, message: "请输入品牌/厂家名称", trigger: "blur" }],
  priceYuan: [{ required: true, message: "请输入维保价格", trigger: "blur" }],
};

function formatPrice(price) {
  return "￥" + ((Number(price) || 0) / 100).toFixed(2);
}

function toCent(value) {
  return Math.round((Number(value) || 0) * 100);
}

function indexMethod(index) {
  return (page.current - 1) * page.size + index + 1;
}

function loadData() {
  loading.value = true;
  getManufacturerListFun({ name: search.name, current: page.current, size: page.size })
    .then((res) => {
      records.value = res.data?.records || [];
      page.total = res.data?.total || 0;
    })
    .finally(() => {
      loading.value = false;
    });
}

function resetSearch() {
  search.name = "";
  page.current = 1;
  loadData();
}

function handleSizeChange() {
  page.current = 1;
  loadData();
}

function openDialog(row) {
  dialog.form = row
    ? { id: row.id, name: row.name, priceYuan: (Number(row.price) || 0) / 100 }
    : { name: "", priceYuan: 0 };
  dialog.visible = true;
}

function save() {
  formRef.value?.validate((valid) => {
    if (!valid) return;
    saving.value = true;
    saveManufacturerFun({
      id: dialog.form.id,
      name: dialog.form.name,
      price: toCent(dialog.form.priceYuan),
    })
      .then((res) => {
        if (res.code === 200) {
          ElMessage.success("保存成功");
          dialog.visible = false;
          loadData();
        } else {
          ElMessage.error(res.msg || "保存失败");
        }
      })
      .finally(() => {
        saving.value = false;
      });
  });
}

function remove(row) {
  ElMessageBox.confirm("确认删除「" + row.name + "」的维保价格？", "删除确认", { type: "warning" }).then(() => {
    deleteManufacturerFun(row.id).then((res) => {
      if (res.code === 200) {
        ElMessage.success("删除成功");
        loadData();
      } else {
        ElMessage.error(res.msg || "删除失败");
      }
    });
  }).catch(() => {});
}

function downloadTemplate() {
  templateLoading.value = true;
  downloadManufacturerTemplateFun()
    .then(() => ElMessage.success("模板下载成功"))
    .finally(() => {
      templateLoading.value = false;
    });
}

function exportData() {
  exporting.value = true;
  exportManufacturerFun({ name: search.name })
    .then(() => ElMessage.success("导出成功"))
    .finally(() => {
      exporting.value = false;
    });
}

function handleImport({ file }) {
  importing.value = true;
  importManufacturerFun(file)
    .then((res) => {
      if (res.code === 200) {
        ElMessage.success(res.msg || "导入成功");
        page.current = 1;
        loadData();
      } else {
        ElMessage.error(res.msg || "导入失败");
      }
    })
    .finally(() => {
      importing.value = false;
    });
}

onMounted(() => {
  loadData();
});
</script>

<style lang="scss" scoped>
.manufacturer-price-page {
  padding: 16px;
}

.page-card {
  border-radius: 6px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.import-tip {
  margin-bottom: 14px;
}

.search-form {
  margin-bottom: 8px;
}

.search-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
}

.pagination-box {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}
</style>
